import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Button } from "@/components/ui/button";
import { Phone, AlertCircle, Shield, Loader2, X } from "lucide-react";
import { maskPhone } from "@/utils/masks";

/**
 * PhoneNumberModalMobile - Mobile-optimized phone number input
 * Full-screen bottom sheet style modal
 */
const PhoneNumberModalMobile = ({ open, onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formik = useFormik({
    initialValues: {
      phone: "",
    },
    validationSchema: yup.object({
      phone: yup
        .string()
        .required("O telefone é obrigatório")
        .matches(
          /^\([1-9]{2}\) (?:[2-8]|9[1-9])[0-9]{3,4}-[0-9]{4}$/,
          "Formato inválido. Use: (11) 99999-9999"
        ),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError("");
        const cleanPhone = values.phone.replace(/\D/g, "");
        await onSubmit(cleanPhone);
      } catch (err) {
        setError(err.message || "Erro ao atualizar telefone. Tente novamente.");
        setLoading(false);
      }
    },
  });

  const handlePhoneChange = (e) => {
    const maskedValue = maskPhone(e.target.value);
    formik.setFieldValue("phone", maskedValue);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      {/* Bottom Sheet */}
      <div
        className="relative mt-auto bg-white rounded-t-3xl animate-slideUp"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-slate-300 rounded-full" />
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="h-12 w-12 rounded-2xl bg-green-100 flex items-center justify-center">
              <Phone className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-slate-900 font-outfit">
                Número de Telefone
              </h2>
              <p className="text-sm text-slate-500 font-outfit">
                Obrigatório para continuar
              </p>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-slate-600 font-outfit mb-6 leading-relaxed">
            Precisamos do seu número para contato e segurança da conta.
            Usaremos para comunicações importantes.
          </p>

          {/* Form */}
          <form onSubmit={formik.handleSubmit}>
            {/* Phone Input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 font-outfit mb-2">
                Telefone de Contato <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="tel"
                  name="phone"
                  inputMode="tel"
                  placeholder="(11) 99999-9999"
                  value={formik.values.phone}
                  onChange={handlePhoneChange}
                  onBlur={formik.handleBlur}
                  disabled={loading}
                  autoFocus
                  className={`
                    w-full px-4 py-4 pl-12 bg-slate-50 border rounded-2xl text-base font-outfit
                    placeholder:text-slate-400
                    focus:outline-none focus:border-green-500 focus:bg-white focus:ring-2 focus:ring-green-500/20
                    transition-all disabled:opacity-50
                    ${
                      formik.touched.phone && formik.errors.phone
                        ? "border-red-500 bg-red-50"
                        : "border-slate-200"
                    }
                  `}
                />
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              </div>
              {formik.touched.phone && formik.errors.phone && (
                <p className="text-sm text-red-500 font-outfit mt-2 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {formik.errors.phone}
                </p>
              )}
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700 font-outfit">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || !formik.values.phone}
              className="w-full h-14 bg-green-600 hover:bg-green-700 text-white rounded-2xl text-base font-semibold font-outfit disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  Salvando...
                </>
              ) : (
                "Salvar e Continuar"
              )}
            </Button>

            {/* Privacy Note */}
            <div className="mt-4 flex items-start gap-2 p-3 bg-slate-50 rounded-xl">
              <Shield className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-slate-500 font-outfit leading-relaxed">
                Seus dados estão protegidos. Ao fornecer seu número, você
                concorda com nossos termos de uso e política de privacidade.
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Slide Up Animation */}
      <style>{`
        @keyframes slideUp {
          0% {
            transform: translateY(100%);
          }
          100% {
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default PhoneNumberModalMobile;
